import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

export const DATE_FORMAT = 'YYYY-MM-DD';

// Accepts both proper ISO 8601 strings and the space-separated, colon-less
// offset format Postgres returns for `timestamptz` columns (e.g.
// "2026-07-15 03:00:00+03"), which the native Date constructor is not
// guaranteed to parse consistently across browsers.
const PARSE_FORMATS = [
  'YYYY-MM-DD HH:mm:ssZ',
  'YYYY-MM-DDTHH:mm:ss.SSSZ',
  'YYYY-MM-DDTHH:mm:ssZ',
  'YYYY-MM-DDTHH:mm:ss.SSS',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DD',
];

// customParseFormat only makes sense for strings - handing it a Date or a
// Dayjs instance (e.g. from a DatePicker's initialValue) alongside a format
// array makes it try to string-match the object and always fail.
export const parseDate = (value?: dayjs.ConfigType) => {
  if (!value) {
    return null;
  }
  const parsed =
    typeof value === 'string' ? dayjs(value, PARSE_FORMATS) : dayjs(value);
  return parsed.isValid() ? parsed : null;
};

export const formatDate = (value?: dayjs.ConfigType): string => {
  const parsed = parseDate(value);
  return parsed ? parsed.format(DATE_FORMAT) : '-';
};

// Same as formatDate, but reads the date/time components as UTC before
// formatting, so a date stored at midnight UTC doesn't shift to the
// previous day for users west of UTC.
export const formatDateUtc = (value?: dayjs.ConfigType): string => {
  if (!value) {
    return '-';
  }
  if (typeof value !== 'string') {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.utc().format(DATE_FORMAT) : '-';
  }
  for (const format of PARSE_FORMATS) {
    const parsed = dayjs.utc(value, format);
    if (parsed.isValid()) {
      return parsed.format(DATE_FORMAT);
    }
  }
  return '-';
};
